import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';
import { COPY } from './billing.copy';

/**
 * The monthly software bill.
 *
 * ── Nothing about Waafi is in here ───────────────────────────────────────
 *
 * No merchant id, no API key, no gateway host, no request envelope. The
 * till asks its own server to take the money and the server does the whole
 * gateway leg with credentials that never leave it. Anything else would put a
 * merchant key inside a bundle that anyone can open with DevTools — the same
 * mistake as shipping a private key in /public.
 *
 * So there are exactly two calls: fetch the plans, and ask for the charge.
 *
 * ── The one thing to understand about the charge ─────────────────────────
 *
 * It is synchronous and it blocks. The server calls the gateway and holds the
 * connection open for as long as somebody takes to find their phone, read the
 * USSD prompt and key a PIN. There is no webhook, no status endpoint and no
 * stored transaction id — the HTTP answer to THIS request is the only place
 * the outcome ever appears. Lose the connection and nothing, anywhere, knows
 * whether the money moved.
 *
 * There is also no idempotency key on the server: the same request sent twice
 * charges twice and records both. Which is why `classifyPaymentError` below
 * separates "the wallet said no" from "we never heard back", and why the
 * second one must never be retried quietly.
 */

const auth = (token) => ({ headers: { authorization: token } });

/**
 * How long to wait before giving up on the charge.
 *
 * Long, on purpose. Somebody has to find their phone, wait for the USSD
 * prompt and type a PIN; 30 seconds — what the phone app allows — makes an
 * ordinary slow customer look like a failure, and a false failure here is
 * expensive because retrying can charge twice.
 */
export const PAY_TIMEOUT_MS = 90000;

/** Plans are small and change rarely; this is fetched once per session. */
export async function fetchSubscriptionPlans({ token }) {
  const { data } = await axios.get(`${constants.baseUrl}/configuration`, {
    ...auth(token),
    timeout: 20000,
  });
  return data?.data?.subscriptionPlans || [];
}

/**
 * Ask the server to take the money.
 *
 * Two fields, and deliberately no amount: the server computes `fee × months`
 * from its own copy of the business, so the price cannot be argued with from
 * this side.
 *
 * Resolves only on a real, confirmed payment. Everything else throws, and
 * `classifyPaymentError` turns it into one of three answers.
 */
export async function paySubscriptionBill({ phoneNumber, subscriptionPlanId, token }) {
  const { data } = await axios.post(
    `${constants.baseUrl}/users/pay-subscription-plan-bill`,
    { phoneNumber, subscriptionPlanId },
    { ...auth(token), timeout: PAY_TIMEOUT_MS }
  );

  /**
   * A 2xx that does not say `paid: true` is not a payment.
   *
   * The phone trusts the status code and, when the body comes back with
   * `paid: false`, sits on "Fiiri Teleefankaaga!" forever with no way out —
   * a real hang in production. Treating anything that is not exactly `true`
   * as a refusal closes that door here.
   */
  if (data?.data?.paid !== true) {
    const error = new Error(data?.message || COPY.genericDecline);
    error.dalabtireKind = 'declined';
    throw error;
  }

  return {
    business: data?.data?.updatedBusiness || null,
    message: data?.message || '',
  };
}

/**
 * What went wrong, in the only three flavours that change what a person
 * should do next.
 *
 *   declined  the wallet answered and refused — wrong PIN, no such number,
 *             not enough balance, cancelled. The server sends the reason in
 *             Somali; show it as-is. Nothing was taken. Safe to try again.
 *
 *   gateway   the server answered but no charge happened — it refused the
 *             request before calling out (400/404), or the gateway did not
 *             answer it (502). Nothing was taken. Safe to try again.
 *
 *   unsure    no answer at all: a timeout, or the connection died. The charge
 *             may or may not have gone through and there is no way to ask.
 *             NOT safe to try again without checking the phone first.
 */
export function classifyPaymentError(error) {
  if (error?.dalabtireKind === 'declined') {
    return { kind: 'declined', message: error.message || COPY.genericDecline };
  }

  const response = error?.response;

  // No response object at all: timeout, dropped socket, DNS. This is the one
  // that needs care.
  if (!response) {
    return { kind: 'unsure', message: '' };
  }

  const serverMessage = response?.data?.message;

  // 402 is the server's own signal that the gateway answered and said no.
  if (response.status === 402) {
    return { kind: 'declined', message: serverMessage || COPY.genericDecline };
  }

  return { kind: 'gateway', message: serverMessage || COPY.genericGateway };
}
